import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import './menu.scss';
import '../../../../src/styles/hiddenProvisional.scss';

import logo from '../../../media/img/wonderaly-logo.svg';
import BackArrow from '../backArrow/backArrow';
import { MENU_ITEMS, MENU_CTA } from './menuData';

const DESKTOP_MIN_WIDTH = 992;

const Menu = ({ customClass = '', setIsOffcanvasOpen }) => {
  const offcanvasRef = useRef(null);
  const navRef = useRef(null);
  const location = useLocation();

  // Qué desplegable está abierto: 'servicios', 'proyectos-recursos' o null
  const [openId, setOpenId] = useState(null);

  const isDesktop = () => window.innerWidth >= DESKTOP_MIN_WIDTH;

  // Avisar a la página cuando se abre/cierra el menú móvil (igual que antes)
  useEffect(() => {
    const offcanvasElement = offcanvasRef.current;
    if (!offcanvasElement) return;

    const handleShow = () => setIsOffcanvasOpen && setIsOffcanvasOpen(true);
    const handleHide = () => {
      setIsOffcanvasOpen && setIsOffcanvasOpen(false);
      setOpenId(null);
    };

    offcanvasElement.addEventListener('shown.bs.offcanvas', handleShow);
    offcanvasElement.addEventListener('hidden.bs.offcanvas', handleHide);

    return () => {
      offcanvasElement.removeEventListener('shown.bs.offcanvas', handleShow);
      offcanvasElement.removeEventListener('hidden.bs.offcanvas', handleHide);
    };
  }, [setIsOffcanvasOpen]);

  // Cerrar el desplegable con la tecla Escape o al hacer clic fuera del menú
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenId(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActive = (href) =>
    location.pathname.toLowerCase() === href.toLowerCase();

  const isGroupActive = (item) =>
    item.columns.some((col) => col.items.some((sub) => isActive(sub.href))) ||
    (item.footerLink && isActive(item.footerLink.href));

  const renderDropdown = (item) => {
    const isOpen = openId === item.id;
    const panelId = `submenu-${item.id}`;

    return (
      <li
        key={item.id}
        className={`nav-item menu-dropdown ${isOpen ? 'is-open' : ''} ${
          isGroupActive(item) ? 'active' : ''
        }`}
        onMouseEnter={() => isDesktop() && setOpenId(item.id)}
        onMouseLeave={() => isDesktop() && setOpenId(null)}
      >
        <button
          type="button"
          className="nav-link menu-dropdown__toggle"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpenId(isOpen ? null : item.id)}
        >
          {item.label}
          <span className="menu-dropdown__arrow" aria-hidden="true" />
        </button>

        <div
          id={panelId}
          className={`menu-dropdown__panel ${
            item.type === 'mega' ? 'menu-dropdown__panel--mega' : ''
          }`}
          hidden={!isOpen}
        >
          <div className="menu-dropdown__box">
            <div className="menu-dropdown__columns">
              {item.columns.map((col, index) => (
                <div className="menu-dropdown__column" key={col.title || index}>
                  {col.title && (
                    <p className="menu-dropdown__title">{col.title}</p>
                  )}
                  <ul>
                    {col.items.map((sub) => (
                      <li key={sub.href}>
                        <a
                          href={sub.href}
                          className={`menu-dropdown__link ${
                            isActive(sub.href) ? 'active' : ''
                          }`}
                          aria-current={isActive(sub.href) ? 'page' : undefined}
                        >
                          <span className="menu-dropdown__label">{sub.label}</span>
                          {sub.desc && (
                            <span className="menu-dropdown__desc">{sub.desc}</span>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {item.footerLink && (
              <a href={item.footerLink.href} className="menu-dropdown__footer">
                {item.footerLink.label} →
              </a>
            )}
          </div>
        </div>
      </li>
    );
  };

  const renderLink = (item) => (
    <li key={item.href} className="nav-item">
      <a
        href={item.href}
        className={`nav-link ${item.highlight ? 'menu-link--highlight' : ''} ${
          isActive(item.href) ? 'active' : ''
        }`}
        aria-current={isActive(item.href) ? 'page' : undefined}
      >
        {item.label}
      </a>
    </li>
  );

  return (
    <nav
      ref={navRef}
      className={`navbar navbar-expand-lg fixed-top ${customClass}`}
      aria-label="Menú principal"
    >
      <div className="container-fluid d-flex justify-content-between align-items-center w-100">
        <div className="logo d-flex align-items-center">
          {location.pathname.startsWith('/case-study/') ||
          location.pathname === '/faq' ? (
            <BackArrow />
          ) : (
            <a href="/" className="d-flex align-items-center text-decoration-none">
                <img src={logo} alt="Wonderaly, ir al inicio" className="menu-logo" />
            </a>
          )}
        </div>

        <button
          className="navbar-toggler d-lg-none"
          type="button"
          data-bs-toggle="offcanvas"
          data-bs-target="#navbarContent"
          aria-controls="navbarContent"
          aria-label="Abrir menú"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div
          className="offcanvas offcanvas-top"
          id="navbarContent"
          ref={offcanvasRef}
          tabIndex="-1"
          aria-label="Menú"
        >
          <div className="offcanvas-header">
            <span className="offcanvas-title">wonderalyinnovations</span>
            <button
              type="button"
              className="btn-close text-reset custom-close-btn"
              data-bs-dismiss="offcanvas"
              aria-label="Cerrar menú"
            ></button>
          </div>

          <div className="offcanvas-body">
            <div className="navbar1 d-flex flex-column flex-lg-row align-items-lg-center w-100 justify-content-end">
              <ul className="navbar-nav menu-list">
                {MENU_ITEMS.map((item) =>
                  item.type === 'link' ? renderLink(item) : renderDropdown(item)
                )}
              </ul>

              <a href={MENU_CTA.href} className="menu-cta">
                {MENU_CTA.label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Menu;